/**
 * @file SentEmailModal.js
 * @version 1.0.2
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-30
 * @modified 2026-10-30
 * @description What an email actually sent — who to, when, whether it was
 *              delivered, its message and every attachment (PO, invoice,
 *              paperwork…) exactly as it left, from the archive
 *              emailService.sendTracked keeps (routes/emails.js).
 *
 *              Pass `emailId`, or `changeLogId` for an EMAIL_SENT history
 *              entry (resolved to its email server-side). PDFs preview here;
 *              other files open in a new tab.
 *
 *              1.0.2 (2026-10-30): the status reads by the shared rule
 *              (utils/emailDelivery) — "complained" and "rejected" read as
 *              "Marked as spam" and "Rejected".
 */

import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import { API_URL } from '../config';
import './SentEmailModal.css';
import PdfCanvasPreview from './PdfCanvasPreview';
import { deliveryStatus, DELIVERY_STATUSES } from '../utils/emailDelivery';

const when = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const size = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);

function SentEmailModal({ isOpen, onClose, emailId = null, changeLogId = null }) {
  const [email, setEmail] = useState(null);
  const [body, setBody] = useState(null);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null); // { id, url, filename }

  useEffect(() => {
    if (!isOpen) return undefined;
    let live = true;
    setEmail(null); setBody(null); setError(null); setPreview(null);
    const get = (u) => fetch(`${API_URL}${u}`, { credentials: 'include' })
      .then(r => r.json().then(d => { if (!r.ok) throw new Error(d.error || 'Failed to load the email'); return d; }));
    (async () => {
      try {
        const id = emailId || (await get(`/api/emails/from-audit/${changeLogId}`)).id;
        const [d, b] = await Promise.all([get(`/api/emails/${id}`), get(`/api/emails/${id}/body`)]);
        if (!live) return;
        setEmail(d.email);
        setBody(b);
      } catch (e) {
        if (live) setError(e.message);
      }
    })();
    return () => { live = false; };
  }, [isOpen, emailId, changeLogId]);

  const fileUrl = (att) => `${API_URL}/api/emails/${email.id}/attachments/${att.id}`;
  const open = (att) => {
    if (/pdf/i.test(att.content_type || '') || /\.pdf$/i.test(att.filename || '')) {
      setPreview({ id: att.id, url: fileUrl(att), filename: att.filename });
    } else {
      window.open(fileUrl(att), '_blank', 'noopener');
    }
  };

  const { label: statusLabel, tone: statusCls } = email?.status ? deliveryStatus(email.status) : DELIVERY_STATUSES.unknown;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Email as sent" size="lg" className="sent-email">
      {error && <p className="se-error">{error}</p>}
      {!error && !email && <LoadingSpinner />}
      {email && (
        <>
          <dl className="se-head">
            <dt>Subject</dt><dd>{email.subject || '—'}</dd>
            <dt>To</dt><dd>{(email.to_addresses || []).join(', ') || '—'}</dd>
            <dt>From</dt><dd>{email.from_address || '—'}</dd>
            <dt>Sent</dt><dd>{when(email.created_at)}{email.sent_by_name ? ` by ${email.sent_by_name}` : ''}</dd>
            <dt>Status</dt>
            <dd><span className={`se-status ${statusCls}`}>{statusLabel}</span>
              {email.last_event_at && <span className="se-muted"> · {when(email.last_event_at)}</span>}
              {email.error_message && <span className="se-error-inline"> · {email.error_message}</span>}
            </dd>
          </dl>

          {!email.archived && (
            <p className="se-notice">No copy of this email was kept — it was sent before emails were archived (30 Sept 2026).</p>
          )}

          {email.attachments?.length > 0 && (
            <div className="se-attachments">
              {email.attachments.map(att => (
                <button key={att.id} type="button" className={`se-att${preview?.id === att.id ? ' active' : ''}`} onClick={() => open(att)}>
                  <span className="material-symbols-rounded">{/pdf/i.test(att.content_type || '') ? 'picture_as_pdf' : 'attach_file'}</span>
                  <span className="se-att-name">{att.filename}</span>
                  <span className="se-muted">{size(att.size_bytes)}</span>
                </button>
              ))}
            </div>
          )}

          {preview ? (
            <div className="se-preview">
              <div className="se-preview-bar">
                <span>{preview.filename}</span>
                <a href={preview.url} target="_blank" rel="noopener noreferrer">Open in new tab</a>
                <button type="button" className="btn-icon" title="Back to the message" onClick={() => setPreview(null)}>
                  <span className="material-symbols-rounded">close</span>
                </button>
              </div>
              <PdfCanvasPreview
                  className="pdf-canvas-fill se-frame"
                  loadKey={preview.url}
                  load={() => fetch(preview.url, { credentials: 'include' }).then(r => { if (!r.ok) throw new Error('Could not load the PDF'); return r.arrayBuffer(); })}
                />
            </div>
          ) : body && (body.html || body.text) ? (
            // Sandboxed: the archived HTML is shown, never allowed to run.
            <iframe title="Email message" className="se-frame se-body" sandbox=""
              srcDoc={body.html || `<pre style="white-space:pre-wrap;font-family:sans-serif">${String(body.text).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))}</pre>`} />
          ) : null}
        </>
      )}
    </Modal>
  );
}

export default SentEmailModal;
